import ApiService from './apiService';
import RequestType from '../constants/requestType';

/**
 * @typedef {Object} ContactPerson
 * @property {number} id
 * @property {string} name
 * @property {string} email
 * @property {string} phone
 * @property {string} salutation - 'Dhr.' | 'Mevr.' | ''
 * @property {string|null} role
 * @property {string|null} connectWiseId
 * @property {boolean} isSynced
 * @property {number} customerId
 */

/**
 * @typedef {Object} TechnicalContactSettings
 * @property {boolean} enabled
 * @property {ContactPerson|null} assignedContact
 * @property {ContactPerson|null} effectiveContact
 * @property {boolean} hasContact
 */

/**
 * @typedef {Object} CreateContactData
 * @property {string} name
 * @property {string} email
 * @property {string} phone
 * @property {string} salutation - 'Dhr.' | 'Mevr.' | ''
 */

/**
 * API service for managing technical contacts on quotations
 */
export default class TechnicalContactApi {
    /**
     * Toggle technical contact feature for a quotation
     * @param {number} quotationId
     * @param {boolean} enabled
     * @returns {Promise<{success: boolean, message: string, enabled: boolean}>}
     */
    static async toggleTechnicalContact(quotationId, enabled) {
        const url = `quotations/${quotationId}/technical-contact/toggle`;
        return await ApiService.performRequest(RequestType.POST, url, { enabled });
    }

    /**
     * Get technical contact settings for a quotation
     * @param {number} quotationId
     * @returns {Promise<TechnicalContactSettings>}
     */
    static async getTechnicalContact(quotationId) {
        const url = `quotations/${quotationId}/technical-contact`;
        return await ApiService.performRequest(RequestType.GET, url);
    }

    /**
     * Select a technical contact for a quotation
     * @param {number} quotationId
     * @param {number} contactId
     * @returns {Promise<{success: boolean, message: string, contact: ContactPerson}>}
     */
    static async selectTechnicalContact(quotationId, contactId) {
        const url = `quotations/${quotationId}/technical-contact/select`;
        return await ApiService.performRequest(RequestType.POST, url, { contactId });
    }

    /**
     * Search for contacts at the quotation's customer
     * @param {number} quotationId
     * @param {string} query
     * @returns {Promise<{contacts: ContactPerson[], total: number}>}
     */
    static async searchContacts(quotationId, query) {
        const url = `quotations/${quotationId}/technical-contact/search?q=${encodeURIComponent(query)}`;
        return await ApiService.performRequest(RequestType.GET, url);
    }

    /**
     * Create a new technical contact
     * @param {number} quotationId
     * @param {CreateContactData} contactData
     * @returns {Promise<{success: boolean, message: string, contact: ContactPerson}>}
     */
    static async createTechnicalContact(quotationId, contactData) {
        const url = `quotations/${quotationId}/technical-contact/create`;
        return await ApiService.performRequest(RequestType.POST, url, contactData);
    }
}
